import { css } from "@emotion/react";
import React, { useEffect, useRef, useState } from "react";
import { Common } from "@src/styles/Common";
import { Grid } from "@mui/material";
import { IRegion, getLocationCortar } from "@src/hooks/newFilter/useGetLocation";
import AiTitle from "@src/components/aiFilter/AiTitle";
import { MAX_Y } from "@src/components/aiFilter/Constants";
import ShowSelectedUseLocValueBox from "@src/pages/mapFilter/ShowSelectedUseLocValueBox";

interface Props {
    locationList: string[];
    onChangeLocation: (val: string[]) => void;
    onSubmitShowToast?: () => void;
}

const LocationFirstStep: React.FC<Props> = ({ locationList, onChangeLocation }) => {
    const [sidoList, setSidoList] = useState<any>();
    const [sigunguList, setSigunguList] = useState<any>();
    const [emdList, setEmdList] = useState<any>();
    const [selectedSido, setSelectedSido] = useState<any>();
    const [selectedSigungu, setSelectedSigungu] = useState<any>();
    const [showLocText, setShowLocText] = useState<any[]>([]);
    const [lastRegionForFirst, setLastRegionForFirst] = useState<string | null>();
    const sidoRef = useRef<any>();

    const makeLocText = async (loc: string) => {
        let sidoText = "";
        let sigunguText = "";
        let emdText = "";
        sidoText = await getLocationCortar("0000000000").then((res) => {
            const selectedSido = res.filter((val: any) => val?.cortarNo?.slice(0, 2) === loc.slice(0, 2));
            return selectedSido?.[0]?.cortarName ?? "";
        });
        if (loc.slice(2) !== "00000000") {
            sigunguText = await getLocationCortar(`${loc.slice(0, 2)}00000000`).then((res) => {
                const selectedSigungu = res.filter((val: any) => val?.cortarNo?.slice(0, 5) === loc.slice(0, 5));
                return selectedSigungu?.[0]?.cortarName;
            });
        } else {
            return { name: `${sidoText} > 전체`, value: loc };
        }
        if (loc.slice(5) !== "00000") {
            emdText = await getLocationCortar(`${loc.slice(0, 5)}00000`).then((res) => {
                const selectedEmd = res.filter((val: any) => val?.cortarNo === loc);
                return selectedEmd?.[0]?.cortarName;
            });
        } else {
            emdText = "전체";
        }
        return { name: `${sidoText} > ${sigunguText} > ${emdText}`, value: loc };
    };

    const settingLocText = async () => {
        const result = await Promise.all(
            locationList.filter((data) => data !== "all")?.map(async (val: any) => await makeLocText(val)),
        );
        setShowLocText(result);
    };

    useEffect(() => {
        if (locationList?.length) {
            settingLocText();
        } else {
            setShowLocText([]);
        }
    }, [locationList]);

    // 조건당 소재지 1개만 선택
    const onClickAllSido = () => {
        onChangeLocation([]);
        setSelectedSigungu(null);
        setSelectedSido(null);
    };

    const onClickSido = (item: IRegion, active: boolean) => {
        if (active) {
            onChangeLocation([]);
            setSelectedSido(null);
            setSelectedSigungu(null);
        } else {
            onChangeLocation([item.cortarNo]);
            setSelectedSido(item);
            setSelectedSigungu(null);
        }
        sidoRef.current?.scrollTo(0, 0);
    };

    const onClickAllSigungu = () => {
        if (selectedSido) {
            onChangeLocation([selectedSido.cortarNo]);
            setSelectedSigungu(null);
        }
    };

    const onClickSigungu = (item: IRegion, active: boolean) => {
        if (active) {
            onChangeLocation(selectedSido ? [selectedSido.cortarNo] : []);
            setSelectedSigungu(null);
        } else {
            onChangeLocation([item.cortarNo]);
            setSelectedSigungu(item);
        }
    };

    const onClickAllEmd = () => {
        if (selectedSigungu) {
            onChangeLocation([selectedSigungu.cortarNo]);
        }
    };

    const onClickEmd = (item: IRegion, active: boolean) => {
        if (active) {
            onChangeLocation(selectedSigungu ? [selectedSigungu.cortarNo] : []);
        } else {
            onChangeLocation([item.cortarNo]);
        }
    };

    useEffect(() => {
        getLocationCortar("0000000000").then((res) => {
            setSidoList(res);
            // 서버/스토어에 저장된 값이 없을 때 임의로 기본 선택을 만들지 않는다.
            if (locationList.length) {
                const lastInvestmentFilterRegion = locationList[locationList.length - 1];
                setLastRegionForFirst(lastInvestmentFilterRegion);
                const newSido = res.filter((val: any) => val.cortarNo.slice(0, 2) === lastInvestmentFilterRegion.slice(0, 2));
                setSelectedSido(newSido?.[0]);
            } else {
                setSelectedSido(null);
                setSelectedSigungu(null);
                setSigunguList([]);
                setEmdList([]);
            }
        });
    }, []);

    useEffect(() => {
        if (selectedSido) {
            getLocationCortar(selectedSido.cortarNo)
                .then((res: any) => {
                    setSigunguList(res);
                    setEmdList([]);
                    if (lastRegionForFirst) {
                        const newSigungu = res.filter((val: any) => val.cortarNo.slice(0, 5) === lastRegionForFirst.slice(0, 5));
                        setSelectedSigungu(newSigungu?.[0]);
                    }
                })
                .then(() => setLastRegionForFirst(null));
        } else {
            setSigunguList([]);
        }
    }, [selectedSido]);

    useEffect(() => {
        if (selectedSigungu) {
            getLocationCortar(selectedSigungu.cortarNo).then((res) => setEmdList(res));
        } else {
            setEmdList([]);
        }
    }, [selectedSigungu]);

    return (
        <div css={rootBoxStyle}>
            <AiTitle>
                <h2>
                    관심있는 <span className="color-text">소재지</span>를 선택해주세요.
                </h2>
            </AiTitle>
            <div css={summaryBoxStyle}>
                {!!showLocText.length &&
                    showLocText.map((locData: any) => (
                        <ShowSelectedUseLocValueBox
                            value={locData.name}
                            onDeleteValue={() => {
                                const newLoc = [...locationList].filter((val) => val !== locData.value);
                                onChangeLocation(newLoc);
                            }}
                            key={locData.name}
                        />
                    ))}
            </div>
            <Grid container css={subTitleBoxStyle}>
                <Grid item xs={3.5}>
                    <p className="subTitleText">시/도</p>
                </Grid>
                <Grid item xs={4.3}>
                    <p className="subTitleText">시/구/군</p>
                </Grid>
                <Grid item xs={4.2}>
                    <p className="subTitleText">동/읍/면</p>
                </Grid>
            </Grid>
            <Grid container css={selectBoxRootStyle}>
                <Grid item xs={3.5} css={selectBoxStyle(MAX_Y)} ref={sidoRef}>
                    {sidoList?.map((item: any) => {
                        const checkActive = locationList?.length
                            ? locationList.some((val) => val?.startsWith(item.cortarNo.slice(0, 2)))
                            : false;
                        return (
                            <button
                                key={item.cortarNo}
                                css={selectButtonStyle(checkActive)}
                                onClick={() => onClickSido(item, checkActive)}
                            >
                                {item.cortarName}
                            </button>
                        );
                    })}
                </Grid>
                {locationList.length > 0 && (
                    <>
                        <Grid item xs={4.3} css={selectBoxStyle(MAX_Y)}>
                            {!!sigunguList?.length && (
                                <button
                                    css={selectButtonStyle(locationList.some((val) => val === selectedSido?.cortarNo))}
                                    onClick={onClickAllSigungu}
                                >
                                    전체
                                </button>
                            )}
                            {sigunguList?.map((item: any) => {
                                const activeListLengthCheck = locationList.some((val) => val === selectedSido?.cortarNo);
                                const checkActive = activeListLengthCheck
                                    ? false
                                    : locationList.some((val) => val.slice(0, 5) === item.cortarNo.slice(0, 5));
                                return (
                                    <button
                                        key={item.cortarNo}
                                        css={selectButtonStyle(checkActive)}
                                        onClick={() => onClickSigungu(item, checkActive)}
                                    >
                                        {item.cortarName}
                                    </button>
                                );
                            })}
                        </Grid>
                        <Grid item xs={4.2} css={selectBoxStyle(MAX_Y)}>
                            {!!emdList?.length && (
                                <button
                                    css={selectButtonStyle(locationList.some((val) => val === selectedSigungu?.cortarNo))}
                                    onClick={onClickAllEmd}
                                >
                                    전체
                                </button>
                            )}
                            {emdList?.map((item: any) => {
                                const activeListLengthCheck = locationList.some((val) => val === selectedSigungu?.cortarNo);
                                const checkActive = activeListLengthCheck
                                    ? false
                                    : locationList.some((val) => val === item.cortarNo);
                                return (
                                    <button
                                        key={item.cortarNo}
                                        css={selectButtonStyle(checkActive)}
                                        onClick={() => onClickEmd(item, checkActive)}
                                    >
                                        {item.cortarName}
                                    </button>
                                );
                            })}
                        </Grid>
                    </>
                )}
            </Grid>
        </div>
    );
};

export default LocationFirstStep;

const rootBoxStyle = css`
    background-color: #ffffff;
    padding: 0px 14px;
`;

const subTitleBoxStyle = css`
    display: flex;
    align-items: center;
    border-bottom: 0.6px solid #b1aeb8;
    font-size: 14px;
    .subTitleText {
        padding: 8px 14px;
        background-color: #ffffff;
        color: #0c002c;
        text-align: center;
        font-size: 14px;
        font-weight: 500;
        line-height: 140%;
        box-sizing: border-box;
        width: 100%;
    }
`;

const selectBoxRootStyle = css`
    display: flex;
    align-items: center;
`;

const selectBoxStyle = (MAXY: number) => css`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: column;
    width: 100%;
    padding: 12px 10px;
    gap: 8px;
    height: ${MAXY - 300}px;
    overflow-y: scroll;
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
        display: none;
    }
`;

const selectButtonStyle = (active: boolean) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    border-radius: 8px;
    padding: 10px;
    background: #fdfafa;
    transition: color 0.3s linear;
    font-size: 14px;
    font-weight: 500;
    ${active && {
        background: Common.colors.aucMarkerColor,
        color: "#ffffff",
    }}
`;

const summaryBoxStyle = css`
    padding: 8px 14px;
    display: flex;
    align-items: center;
    width: 100vw;
    overflow-x: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
        display: none;
    }
`;
